"use client";
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../Header';
import Footer from '../Footer';

const PrivacyPolicy = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Floating food elements data with emojis
  const floatingElements = [
    { emoji: '🧁', size: 'text-4xl', delay: 0 },
    { emoji: '🍰', size: 'text-5xl', delay: 1 },
    { emoji: '🍪', size: 'text-3xl', delay: 2 },
    { emoji: '🥧', size: 'text-4xl', delay: 3 },
    { emoji: '🍩', size: 'text-3xl', delay: 4 },
    { emoji: '🧁', size: 'text-4xl', delay: 5 },
    { emoji: '🍰', size: 'text-5xl', delay: 6 },
    { emoji: '🍪', size: 'text-3xl', delay: 7 },
  ];

  const PolicySection = ({ title, children, icon, delay = 0 }) => (
    <motion.section
      className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-8 border-2 border-orange-200 relative overflow-hidden"
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-100 to-orange-100 rounded-full opacity-30 -translate-y-10 translate-x-10" />
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-orange-100 to-yellow-100 rounded-full opacity-30 translate-y-8 -translate-x-8" />

      <motion.header
        className="flex items-center gap-4 mb-6 relative z-10"
        whileHover={{ scale: 1.02 }}
      >
        <motion.span
          className="text-3xl"
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {icon}
        </motion.span>
        <h2
          className="text-2xl font-bold text-red-500"
          style={{
            fontFamily: "'Sansita Swashed', cursive",
            textShadow: "1px 1px 2px rgba(255,255,255,0.8)"
          }}
        >
          {title}
        </h2>
      </motion.header>

      <div className="relative z-10 text-slate-700 leading-relaxed">
        {children}
      </div>
    </motion.section>
  );

  const ContactCard = ({ icon, text, delay = 0 }) => (
    <motion.div
      className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-100 to-red-100 rounded-xl border border-orange-200"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay }}
      whileHover={{ scale: 1.02, x: 5 }}
    >
      <span className="text-2xl">{icon}</span>
      <span className="text-cyan-900 font-medium">{text}</span>
    </motion.div>
  );

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-rose-50 to-orange-100" />

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23dc2626' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }} />
        </div>

        {/* Floating Food Emoji Elements */}
        {floatingElements.map((element, index) => (
          <motion.div
            key={`emoji-${index}`}
            className={`absolute ${element.size} opacity-15 pointer-events-none`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              filter: 'blur(0.5px)',
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, Math.random() * 20 - 10, 0],
              rotate: [0, Math.random() * 20 - 10, 0],
            }}
            transition={{
              duration: 6 + Math.random() * 4,
              repeat: Infinity,
              delay: element.delay,
              ease: "easeInOut",
            }}
          >
            {element.emoji}
          </motion.div>
        ))}

        {/* Large Background Cake Elements with Slow Movement */}
        <motion.div
          className="absolute opacity-6 pointer-events-none"
          style={{
            left: `${Math.random() * 60}%`,
            top: `${Math.random() * 60}%`,
            filter: 'blur(1px)',
          }}
          animate={{
            x: [0, 100, -50, 150, 0],
            y: [0, -80, 120, -40, 0],
            rotate: [0, 5, -3, 8, 0],
            scale: [1, 1.05, 0.95, 1.1, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <div className="w-64 h-64 bg-gradient-to-br from-orange-200 to-red-200 rounded-full flex items-center justify-center text-8xl opacity-25">
            🎂
          </div>
        </motion.div>

        <motion.div
          className="absolute opacity-6 pointer-events-none"
          style={{
            right: `${Math.random() * 60}%`,
            bottom: `${Math.random() * 60}%`,
            filter: 'blur(1px)',
          }}
          animate={{
            x: [0, -120, 80, -60, 0],
            y: [0, 90, -110, 70, 0],
            rotate: [0, -5, 7, -3, 0],
            scale: [1, 1.1, 0.9, 1.05, 1],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5,
          }}
        >
          <div className="w-48 h-48 bg-gradient-to-br from-pink-200 to-purple-200 rounded-full flex items-center justify-center text-6xl opacity-25">
            🧁
          </div>
        </motion.div>

        {/* Additional Floating Cookie Elements */}
        <motion.div
          className="absolute opacity-8 pointer-events-none"
          style={{
            left: `${Math.random() * 40}%`,
            top: `${Math.random() * 70 + 15}%`,
            filter: 'blur(0.8px)',
          }}
          animate={{
            x: [0, 60, -40, 80, 0],
            y: [0, -50, 70, -30, 0],
            rotate: [0, 15, -10, 20, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3,
          }}
        >
          <div className="w-32 h-32 bg-gradient-to-br from-yellow-200 to-orange-300 rounded-full flex items-center justify-center text-4xl opacity-70">
            🍪
          </div>
        </motion.div>

        <motion.div
          className="absolute opacity-8 pointer-events-none"
          style={{
            right: `${Math.random() * 40}%`,
            top: `${Math.random() * 50 + 25}%`,
            filter: 'blur(0.8px)',
          }}
          animate={{
            x: [0, -70, 50, -90, 0],
            y: [0, 60, -80, 40, 0],
            rotate: [0, -12, 18, -8, 0],
          }}
          transition={{
            duration: 22,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 7,
          }}
        >
          <div className="w-28 h-28 bg-gradient-to-br from-pink-200 to-red-300 rounded-full flex items-center justify-center text-3xl opacity-70">
            🥧
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        <Header />

        {/* Hero Section */}
        <motion.section
          className="relative py-10 px-10 text-center"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Backdrop overlay for text readability */}
          <div className="absolute inset-0 bg-white/30 backdrop-blur-[1 px] z-5" />

          <div className="relative z-20">
            <motion.h1
              className="text-6xl md:text-7xl font-bold text-cyan-900 mb-6 drop-shadow-lg"
              style={{
                fontFamily: "'Sansita Swashed', cursive",
                textShadow: "2px 2px 4px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.5)"
              }}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Privacy Policy
            </motion.h1>
            <motion.p
              className="text-xl text-slate-600 max-w-2xl mx-auto mb-8 drop-shadow-sm"
              style={{
                textShadow: "1px 1px 2px rgba(255,255,255,0.8)"
              }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Your privacy is important to us. Learn how we protect and handle your personal information.
            </motion.p>

            {/* Decorative Elements */}
            <div className="flex justify-center items-center gap-6 mb-8">
              <motion.div
                className="w-16 h-1 bg-gradient-to-r from-red-500 to-orange-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: 64 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              />
              <motion.div
                className="text-3xl bg-emerald-200 rounded-full p-4 inline-flex items-center justify-center"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                🔒
              </motion.div>
              <motion.div
                className="w-16 h-1 bg-gradient-to-l from-red-500 to-orange-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: 64 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              />
            </div>

            {/* Last Updated */}
            <motion.p
              className="text-sm text-slate-500 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-full inline-block"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              Last Updated: December 2024
            </motion.p>
          </div>
        </motion.section>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-6 pb-20 relative z-10">
          <div className="space-y-8">
            {/* Information We Collect */}
            <PolicySection title="Information We Collect" icon="📋" delay={0.1}>
              <p className="mb-4">We may collect personal information such as:</p>
              <ul className="space-y-2 ml-6">
                <motion.li
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <span className="text-red-500">🍰</span> Your name
                </motion.li>
                <motion.li
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <span className="text-red-500">📧</span> Email address
                </motion.li>
                <motion.li
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <span className="text-red-500">📞</span> Phone number
                </motion.li>
                <motion.li
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <span className="text-red-500">🏠</span> Delivery address
                </motion.li>
                <motion.li
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <span className="text-red-500">🛒</span> Order details and preferences
                </motion.li>
              </ul>
              <p className="mt-4 text-sm bg-orange-50 p-3 rounded-lg border-l-4 border-orange-300">
                This information is only collected when you voluntarily fill out a form or send us a message via WhatsApp.
              </p>
            </PolicySection>

            {/* How We Use Your Information */}
            <PolicySection title="How We Use Your Information" icon="🔧" delay={0.2}>
              <p className="mb-4">We use your information to:</p>
              <ul className="space-y-2 ml-6">
                <motion.li
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <span className="text-red-500">✅</span> Process and confirm your orders
                </motion.li>
                <motion.li
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <span className="text-red-500">💬</span> Respond to your inquiries
                </motion.li>
                <motion.li
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <span className="text-red-500">📢</span> Share updates or offers (only if you opt-in)
                </motion.li>
                <motion.li
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <span className="text-red-500">⭐</span> Improve our customer service
                </motion.li>
              </ul>
              <p className="mt-4 text-sm bg-green-50 p-3 rounded-lg border-l-4 border-green-300 font-medium">
                We do not sell, trade, or rent your personal information to third parties.
              </p>
            </PolicySection>

            {/* WhatsApp Communication */}
            <PolicySection title="WhatsApp Communication" icon="💚" delay={0.3}>
              <p>
                If you place orders or contact us via WhatsApp, your messages are subject to WhatsApp's privacy policy.
                We do not store or misuse your chat information.
              </p>
            </PolicySection>

            {/* Cookies */}
            <PolicySection title="Cookies" icon="🍪" delay={0.4}>
              <p>
                We may use cookies to enhance your experience on our website. You can choose to disable cookies
                through your browser settings.
              </p>
            </PolicySection>

            {/* Data Protection */}
            <PolicySection title="Data Protection" icon="🛡️" delay={0.5}>
              <p>
                We take reasonable steps to protect your personal data, but no method of transmission over the
                Internet is 100% secure.
              </p>
            </PolicySection>

            {/* Your Consent */}
            <PolicySection title="Your Consent" icon="✋" delay={0.6}>
              <p>
                By using our website, you consent to our privacy policy.
              </p>
            </PolicySection>

            {/* Changes to This Policy */}
            <PolicySection title="Changes to This Policy" icon="📝" delay={0.7}>
              <p>
                We may update this privacy policy from time to time. Any changes will be posted on this page
                with a revised "Last Updated" date.
              </p>
            </PolicySection>

            {/* Contact Us */}
            <PolicySection title="Contact Us" icon="📞" delay={0.8}>
              <p className="mb-6">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <div className="space-y-4">
                <ContactCard
                  icon="📧"
                  text="iynacakes@gmail.com"
                  delay={0.9}
                />
                <ContactCard
                  icon="💚"
                  text="Or message us directly via WhatsApp from the site"
                  delay={1.0}
                />
              </div>
            </PolicySection>
          </div>

          {/* Back to Top Button */}
          <motion.button
            className="fixed bottom-8 right-8 bg-gradient-to-r from-red-500 to-orange-500 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-20"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            whileHover={{ scale: 1.1, rotate: 360 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.2 }}
          >
            <span className="text-2xl">🔝</span>
          </motion.button>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default PrivacyPolicy;
